import { useEffect, useMemo, useRef, useState } from 'react'
import { useRoster, type TabKey } from '../lib/roster'
import { splitBySelection } from '../lib/selection'

/**
 * The mailing list — build a group of agents to email, across as many filters
 * as it takes.
 *
 * **The count lives on the button, not inside the menu.** Because the selection
 * survives a filter change, agents can be ticked while not visible, and that is
 * the normal state rather than an edge case: filter to Charleston area, add
 * them, filter to Charleston MLS, add those. A count she has to open a menu to
 * read is a count she will forget.
 *
 * `Show only these` is how the rest of the app reaches the list. It narrows the
 * roster to the ticked agents, so Export and Tag — which both act on whatever
 * is on screen — need no changes at all to work on a mailing list. "Which
 * agents" stays one function.
 */
export default function SelectionMenu({ tab }: { tab: TabKey }) {
  const r = useRoster()
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [confirmClear, setConfirmClear] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  useEffect(() => {
    setNote(null)
    setConfirmClear(false)
  }, [tab, open])

  const rows = open ? r.visible(tab) : []
  const { already, fresh } = useMemo(() => splitBySelection(rows, r.selection), [rows, r.selection])

  const add = () => {
    const total = rows.length
    const n = r.addToSelection(fresh)
    // "Added 7. 6 of those were already on the list" is arithmetically
    // impossible and it is the sentence she reads to decide whether the overlap
    // was handled. The already-count is of what was on screen, not of what was
    // added, and the wording has to say so.
    setNote(
      already.length > 0
        ? `Added ${n}. ${already.length} of the ${total} on screen ${already.length === 1 ? 'was' : 'were'} already on the list.`
        : `Added ${n}.`,
    )
  }

  const drop = () => {
    const n = r.removeFromSelection(already)
    setNote(`Took ${n} off the list. Undo is below if that was not meant.`)
  }

  return (
    <div className="exportwrap" ref={box}>
      <button
        className={`btn sm ${r.selectedCount > 0 ? '' : 'ghost'}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Mailing list{r.selectedCount > 0 ? ` · ${r.selectedCount}` : ''}
      </button>

      {open && (
        <div className="exportmenu">
          <p className="cnt">
            {r.selectedCount === 0
              ? 'Nobody on the list yet'
              : `${r.selectedCount} on the list`}
          </p>

          {rows.length === 0 ? (
            <p className="hint">No agents match, so there is nothing to add.</p>
          ) : (
            <>
              <p className="hint">
                {rows.length} {rows.length === 1 ? 'agent matches' : 'agents match'} what is on
                screen
                {already.length > 0 && (
                  <>
                    {' · '}
                    <strong>{already.length}</strong> already on the list
                  </>
                )}
              </p>

              <button className="btn sm" disabled={fresh.length === 0} onClick={add}>
                {fresh.length === 0 ? 'All of these are on the list' : `Add ${fresh.length}`}
              </button>
              <p className="hint">
                Adds everyone on screen who is not already on the list. Filter another way and add
                again — nobody is added twice.
              </p>

              <button className="btn sm" disabled={already.length === 0} onClick={drop}>
                {already.length === 0 ? 'None of these are on the list' : `Take off ${already.length}`}
              </button>
            </>
          )}

          <div aria-live="polite">{note && <p className="cnt">{note}</p>}</div>

          {r.selectedCount > 0 && (
            <>
              <label className="chk stack">
                <input
                  type="checkbox"
                  checked={r.selectedOnly}
                  onChange={(e) => r.setSelectedOnly(e.target.checked)}
                />
                Show only these {r.selectedCount}
              </label>
              <p className="hint">
                Read the list before you send to it. While this is on, Export and Tag act on the
                mailing list too.
              </p>

              {confirmClear ? (
                <>
                  <p className="cnt">Clear all {r.selectedCount}?</p>
                  <button
                    className="btn sm"
                    onClick={() => {
                      r.clearSelection()
                      setConfirmClear(false)
                      setNote(null)
                    }}
                  >
                    Yes, clear it
                  </button>
                  <button className="btn ghost sm" onClick={() => setConfirmClear(false)}>
                    Keep it
                  </button>
                </>
              ) : (
                <button className="btn sm" onClick={() => setConfirmClear(true)}>
                  Clear all {r.selectedCount}
                </button>
              )}
            </>
          )}

          {r.undoSelection && (
            <>
              <p className="cnt">
                {r.undoSelection.what === 'sent'
                  ? `${r.undoSelection.count} ${r.undoSelection.count === 1 ? 'person has' : 'people have'} just been emailed.`
                  : r.undoSelection.what === 'cleared'
                    ? `The list was cleared — ${r.undoSelection.count} ${r.undoSelection.count === 1 ? 'person' : 'people'}.`
                    : `${r.undoSelection.count} ${r.undoSelection.count === 1 ? 'person was' : 'people were'} taken off.`}
              </p>
              <button className="btn sm" onClick={() => r.undoSelection?.run()}>
                Put {r.undoSelection.what === 'removed' ? 'those' : 'them'} back
              </button>
              {/*
                The warning changes with the cause. After a clear, putting them
                back is free. After a send it re-loads a group that has already
                had this email, and the toolbar goes straight back to reading
                "Email 40" — which is the shape of an accidental second send.
              */}
              <p className="hint">
                {r.undoSelection.what === 'sent'
                  ? 'Putting them back re-loads the group you just emailed — useful for a different follow-up, not for sending the same thing again. Ticking anyone before you press this loses the undo.'
                  : 'Ticking anyone before you press this loses the undo.'}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  )
}
